import { useRef, useCallback, lazy, Suspense } from "react"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Button } from "@/components/ui/button"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { Separator } from "@/components/ui/separator"
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  ImageIcon,
  Trash2Icon,
  LoaderIcon,
} from "lucide-react"
import { readFileAsDataUrl } from "./helpers"
import type { CoversheetData } from "@/components/coversheet-document"

const CoversheetPreview = lazy(
  () => import("@/components/coversheet-preview")
)

type CoversheetBuilderStepProps = {
  coversheet: CoversheetData
  updateCoversheet: (field: keyof CoversheetData, value: string) => void
  setCoversheet: React.Dispatch<React.SetStateAction<CoversheetData>>
  previewCoversheet: CoversheetData
  toNumbers: string[]
  previewRecipientIndex: number
  setPreviewRecipientIndex: React.Dispatch<React.SetStateAction<number>>
  contactNames: Record<string, string>
  calculatedDate: string
  calculatedPages: string
}

export function CoversheetBuilderStep({
  coversheet,
  updateCoversheet,
  setCoversheet,
  previewCoversheet,
  toNumbers,
  previewRecipientIndex,
  setPreviewRecipientIndex,
  contactNames,
  calculatedDate,
  calculatedPages,
}: CoversheetBuilderStepProps) {
  const logoInputRef = useRef<HTMLInputElement>(null)

  const handleLogoUpload = useCallback(
    async (file: File) => {
      const dataUrl = await readFileAsDataUrl(file)
      setCoversheet((prev) => ({ ...prev, logoUrl: dataUrl }))
    },
    [setCoversheet]
  )

  return (
    <div className="grid grid-cols-[1fr_2fr] gap-6 min-h-[540px]">
      {/* Form side */}
      <div className="space-y-4 overflow-y-auto pr-2">
        {/* Recipient cycling */}
        {toNumbers.length > 1 && (
          <div className="flex items-center gap-2 rounded-lg border bg-muted/30 px-3 py-2">
            <p className="text-xs text-muted-foreground flex-1">
              Previewing for:{" "}
              <span className="font-mono text-foreground font-medium">
                {contactNames[
                  toNumbers[
                    Math.min(previewRecipientIndex, toNumbers.length - 1)
                  ]
                ] ??
                  toNumbers[
                    Math.min(previewRecipientIndex, toNumbers.length - 1)
                  ]}
              </span>
              <span className="ml-1.5 text-muted-foreground">
                (
                {Math.min(previewRecipientIndex, toNumbers.length - 1) + 1} of{" "}
                {toNumbers.length})
              </span>
            </p>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="size-7"
              disabled={previewRecipientIndex <= 0}
              onClick={() =>
                setPreviewRecipientIndex((i) => Math.max(0, i - 1))
              }
            >
              <ChevronLeftIcon className="size-4" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="size-7"
              disabled={previewRecipientIndex >= toNumbers.length - 1}
              onClick={() =>
                setPreviewRecipientIndex((i) =>
                  Math.min(toNumbers.length - 1, i + 1)
                )
              }
            >
              <ChevronRightIcon className="size-4" />
            </Button>
          </div>
        )}

        {/* Logo upload */}
        <div className="space-y-1.5">
          <Label className="text-xs">Logo</Label>
          <div className="flex items-center gap-3">
            {coversheet.logoUrl ? (
              <div className="relative size-14 rounded-md border overflow-hidden bg-muted/30 shrink-0">
                <img
                  src={coversheet.logoUrl}
                  alt="Coversheet logo"
                  className="size-full object-contain"
                  crossOrigin="anonymous"
                />
                <button
                  type="button"
                  className="absolute inset-0 flex items-center justify-center bg-background/60 opacity-0 hover:opacity-100 transition-opacity"
                  onClick={() => updateCoversheet("logoUrl", "")}
                >
                  <Trash2Icon className="size-4 text-destructive" />
                </button>
              </div>
            ) : (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-14 w-14 flex-col gap-1"
                onClick={() => logoInputRef.current?.click()}
              >
                <ImageIcon className="size-4 text-muted-foreground" />
                <span className="text-[10px] text-muted-foreground">
                  Upload
                </span>
              </Button>
            )}
            <input
              ref={logoInputRef}
              type="file"
              accept=".png,.jpg,.jpeg,.svg,.webp"
              className="hidden"
              onChange={async (e) => {
                const file = e.target.files?.[0]
                if (file) await handleLogoUpload(file)
                e.target.value = ""
              }}
            />
            <p className="text-xs text-muted-foreground">
              Appears in the top-right of the cover sheet.
            </p>
          </div>
        </div>

        <Separator />

        {/* From fields */}
        <fieldset className="space-y-3">
          <legend className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Sender
          </legend>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Name</Label>
              <Input
                placeholder="Your name"
                value={coversheet.fromName}
                onChange={(e) => updateCoversheet("fromName", e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Company</Label>
              <Input
                placeholder="Company"
                value={coversheet.fromCompany}
                onChange={(e) =>
                  updateCoversheet("fromCompany", e.target.value)
                }
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Phone</Label>
              <Input
                placeholder="Phone"
                value={coversheet.fromPhone}
                onChange={(e) => updateCoversheet("fromPhone", e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Fax</Label>
              <Input
                placeholder="Fax number"
                value={coversheet.fromFax}
                onChange={(e) => updateCoversheet("fromFax", e.target.value)}
              />
            </div>
          </div>
        </fieldset>

        <Separator />

        {/* Details */}
        <fieldset className="space-y-3">
          <legend className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Details
          </legend>
          <div className="space-y-1.5">
            <Label className="text-xs">Subject</Label>
            <Input
              placeholder="Subject line"
              value={coversheet.subject}
              onChange={(e) => updateCoversheet("subject", e.target.value)}
            />
          </div>

          {/* Auto-calculated info */}
          <div className="flex items-center gap-4 text-xs text-muted-foreground">
            <span>
              Date: <span className="text-foreground">{calculatedDate}</span>
            </span>
            <span>
              Pages:{" "}
              <span className="text-foreground">
                {calculatedPages} (incl. cover)
              </span>
            </span>
          </div>

          {/* Priority */}
          <div className="space-y-2">
            <Label className="text-xs">Priority</Label>
            <RadioGroup
              value={coversheet.priority}
              onValueChange={(v) =>
                updateCoversheet(
                  "priority",
                  v as CoversheetData["priority"]
                )
              }
              className="flex gap-4"
            >
              {(["urgent", "review", "reply", "comment"] as const).map((p) => (
                <div key={p} className="flex items-center gap-1.5">
                  <RadioGroupItem value={p} id={`pri-${p}`} />
                  <Label
                    htmlFor={`pri-${p}`}
                    className="text-xs font-normal cursor-pointer capitalize"
                  >
                    {p}
                  </Label>
                </div>
              ))}
            </RadioGroup>
          </div>
        </fieldset>

        <Separator />

        {/* Message */}
        <div className="space-y-1.5">
          <Label className="text-xs">Notes / Comments</Label>
          <Textarea
            placeholder="Enter any notes or comments for the recipient..."
            rows={4}
            value={coversheet.message}
            onChange={(e) => updateCoversheet("message", e.target.value)}
          />
        </div>
      </div>

      {/* Preview side -- wider */}
      <div className="flex flex-col gap-2">
        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Preview
        </p>
        <div className="flex-1 rounded-lg border bg-muted/30 overflow-hidden min-h-[540px]">
          <Suspense
            fallback={
              <div className="flex items-center justify-center h-full min-h-[300px]">
                <LoaderIcon className="size-5 animate-spin text-muted-foreground" />
              </div>
            }
          >
            <CoversheetPreview data={previewCoversheet} />
          </Suspense>
        </div>
      </div>
    </div>
  )
}
